import type { OperatorDefinition } from "./types";

import type { RequestLogStatus } from "@/types/request-status";

export const STRING_OPERATORS: OperatorDefinition[] = [
  { operator: "is", display: "is", valueInput: "text" },
  { operator: "is_not", display: "is not", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "contains", display: "includes", valueInput: "text" },
  { operator: "not_contains", display: "does not include", valueInput: "text" },
  { operator: "starts_with", display: "starts with", valueInput: "text" },
  { operator: "ends_with", display: "ends with", valueInput: "text" },
];

export const NUMERIC_OPERATORS: OperatorDefinition[] = [
  { operator: "eq", display: "=", valueInput: "number" },
  { operator: "neq", display: "≠", valueInput: "number" },
  { operator: "gt", display: ">", valueInput: "number" },
  { operator: "gte", display: "≥", valueInput: "number" },
  { operator: "lt", display: "<", valueInput: "number" },
  { operator: "lte", display: "≤", valueInput: "number" },
  { operator: "between", display: "between", valueInput: "number" },
  { operator: "is_null", display: "is empty", valueInput: "none" },
  { operator: "is_not_null", display: "is not empty", valueInput: "none" },
];

export const DATETIME_OPERATORS: OperatorDefinition[] = [
  { operator: "is", display: "is", valueInput: "date" },
  { operator: "before", display: "is before", valueInput: "date" },
  { operator: "after", display: "is after", valueInput: "date" },
  { operator: "between", display: "is between", valueInput: "daterange" },
];

export const BOOLEAN_OPERATORS: OperatorDefinition[] = [
  { operator: "is_true", display: "Yes", valueInput: "none" },
  { operator: "is_false", display: "No", valueInput: "none" },
];

export const ARRAY_OPERATORS: OperatorDefinition[] = [
  { operator: "contains", display: "is", valueInput: "text" },
  { operator: "not_contains", display: "is not", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "not_in", display: "is not one of", valueInput: "text" },
  { operator: "is_empty", display: "is empty", valueInput: "none" },
  { operator: "is_not_empty", display: "is not empty", valueInput: "none" },
];

export const PROMPT_OPERATORS: OperatorDefinition[] = [
  { operator: "is", display: "is", valueInput: "text" },
  { operator: "is_not", display: "is not", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "not_in", display: "is not one of", valueInput: "text" },
  { operator: "is_null", display: "is empty", valueInput: "none" },
  { operator: "is_not_null", display: "is not empty", valueInput: "none" },
];

export const ID_OPERATORS: OperatorDefinition[] = [
  { operator: "is", display: "is", valueInput: "text" },
  { operator: "is_not", display: "is not", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "not_in", display: "is not one of", valueInput: "text" },
];

export const MODEL_OPERATORS: OperatorDefinition[] = [
  { operator: "is", display: "is", valueInput: "text" },
  { operator: "is_not", display: "is not", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "not_in", display: "is not one of", valueInput: "text" },
];

export const PROVIDER_OPERATORS: OperatorDefinition[] = [
  { operator: "is", display: "is", valueInput: "text" },
  { operator: "is_not", display: "is not", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "not_in", display: "is not one of", valueInput: "text" },
];

export const STATUS_OPERATORS: OperatorDefinition[] = [
  { operator: "is", display: "is", valueInput: "text" },
  { operator: "is_not", display: "is not", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "not_in", display: "is not one of", valueInput: "text" },
];

export const INTENT_OPERATORS: OperatorDefinition[] = [
  { operator: "is", display: "is", valueInput: "text" },
  { operator: "is_not", display: "is not", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "not_in", display: "is not one of", valueInput: "text" },
];

export const NESTED_OPERATORS: OperatorDefinition[] = [
  { operator: "key_equals", display: "is", valueInput: "text" },
  { operator: "key_not_equals", display: "is not", valueInput: "text" },
  { operator: "key_contains", display: "includes", valueInput: "text" },
  { operator: "key_not_contains", display: "does not include", valueInput: "text" },
  { operator: "in", display: "is one of", valueInput: "text" },
  { operator: "not_in", display: "is not one of", valueInput: "text" },
  { operator: "is_empty", display: "is empty", valueInput: "none" },
  { operator: "is_not_empty", display: "is not empty", valueInput: "none" },
];

export const SIGNAL_OPERATORS: OperatorDefinition[] = [
  { operator: "key_equals", display: "is", valueInput: "text" },
  { operator: "key_not_equals", display: "is not", valueInput: "text" },
  { operator: "is_empty", display: "is empty", valueInput: "none" },
  { operator: "is_not_empty", display: "is not empty", valueInput: "none" },
];

export const TEXT_SEARCH_OPERATORS: OperatorDefinition[] = [
  { operator: "contains", display: "includes", valueInput: "text" },
  { operator: "not_contains", display: "does not include", valueInput: "text" },
  { operator: "starts_with", display: "starts with", valueInput: "text" },
  { operator: "ends_with", display: "ends with", valueInput: "text" },
];

/** Operators that use partial/text search rather than exact matching. */
export const PARTIAL_MATCH_OPERATORS = new Set([
  "key_contains",
  "key_not_contains",
  "contains",
  "not_contains",
]);

export const ALL_STATUSES: RequestLogStatus[] = ["SUCCESS", "WARNING", "ERROR"];

export const MAX_VISIBLE_FILTER_MULTI_VALUES = 5;

export const ALL_USER_INTENTS = ["frustrated", "satisfied", "curious"] as const;
export const ALL_AGENT_INTENTS = ["apologetic", "refusal", "uncertain"] as const;

export type UserIntentValue = (typeof ALL_USER_INTENTS)[number];
export type AgentIntentValue = (typeof ALL_AGENT_INTENTS)[number];

export { getIntentDisplayLabel } from "./intentLabels";
